/**
 * Agent commission: default % is display / reporting only — it is NOT added to the customer total.
 * Only the incremental percentage above the rate-source floor is added to the base booking total.
 *
 * Sabre GDS (100): 10% company commission floor
 * Booking.com (113): 7% company commission floor
 *
 * @param {Object} opts
 * @param {number} opts.baseBookingTotal - Stay total after tax (matches cart price / totalWithProcessingFees)
 * @param {number} opts.markupPercentage - Total commission rate shown to agent (clamped to floor–100)
 * @param {boolean} opts.markupEnabled - Profile "markup feature" at add time
 * @param {string} [opts.selectionId] - Room+rate key for per-room overrides (browser session)
 */

/** Sabre GDS default commission floor. */
export const GDS_DEFAULT_COMMISSION_PCT = 10
/** Booking.com default commission floor. */
export const BOOKING_COM_DEFAULT_COMMISSION_PCT = 7
/** @deprecated Use GDS_DEFAULT_COMMISSION_PCT or getDefaultCommissionPctForRateSource */
export const DEFAULT_COMMISSION_PCT = GDS_DEFAULT_COMMISSION_PCT

/** Off-market stay default commission floor (matches offMarketSpecialCommission.js). */
export const OFF_MARKET_STAY_COMMISSION_PCT = 6

const round2 = (n) => Math.round(n * 100) / 100

/** Avoid float drift (e.g. 6.1 − 6 → 0.099999…) when showing % and computing incremental $. */
function roundIncrementalPctPoints(raw) {
  return parseFloat(Math.max(0, Number(raw) || 0).toFixed(4))
}

/** Standard commission floor by Sabre rate source code. */
export function getDefaultCommissionPctForRateSource(rateSource) {
  return String(rateSource) === '113' ? BOOKING_COM_DEFAULT_COMMISSION_PCT : GDS_DEFAULT_COMMISSION_PCT
}

/** Resolve floor from rate plan / cart item shape. */
export function resolveDefaultCommissionPct({ rateSource, RateSource, defaultCommissionPct, isOffMarketStay } = {}) {
  if (isOffMarketStay === true) {
    return OFF_MARKET_STAY_COMMISSION_PCT
  }
  if (defaultCommissionPct != null && !Number.isNaN(Number(defaultCommissionPct))) {
    return Number(defaultCommissionPct)
  }
  const src = rateSource ?? RateSource
  if (src != null && src !== '') return getDefaultCommissionPctForRateSource(src)
  return GDS_DEFAULT_COMMISSION_PCT
}

export function clampAgentMarkupPct(pct, minPct = GDS_DEFAULT_COMMISSION_PCT) {
  const floor = Number(minPct)
  const safeFloor = Number.isNaN(floor) ? GDS_DEFAULT_COMMISSION_PCT : floor
  const n = pct === null || pct === undefined ? safeFloor : Number(pct)
  return Number.isNaN(n) ? safeFloor : Math.min(100, Math.max(safeFloor, n))
}

/** Percentage points above the commission floor that are charged on top of the base price. */
export function getIncrementalMarkupPct(totalPct, baselinePct = GDS_DEFAULT_COMMISSION_PCT) {
  const floor = resolveDefaultCommissionPct({ defaultCommissionPct: baselinePct })
  return roundIncrementalPctPoints(clampAgentMarkupPct(totalPct, floor) - floor)
}

/**
 * Full breakdown for UI and cart persistence.
 * @returns {object} markupAmount / totalAgentPays use incremental logic.
 */
export function getAgentMarkupBreakdown(
  baseBookingTotal,
  markupPercentage,
  markupEnabled,
  { defaultCommissionPct = GDS_DEFAULT_COMMISSION_PCT } = {}
) {
  const floor = resolveDefaultCommissionPct({ defaultCommissionPct })
  const pct = clampAgentMarkupPct(markupPercentage, floor)
  const base = Number(baseBookingTotal) || 0
  const defaultCommissionAmount = round2((base * floor) / 100)
  const totalCommissionAmount = round2((base * pct) / 100)
  const incrementalPct = getIncrementalMarkupPct(pct, floor)
  const incrementalAmount = markupEnabled ? round2((base * incrementalPct) / 100) : 0
  const totalYouPay = round2(base + incrementalAmount)
  return {
    markupPercentage: pct,
    defaultCommissionPct: floor,
    defaultCommissionAmount,
    totalCommissionAmount,
    incrementalPct,
    /** Dollar amount added to the customer/agent payable total (same as snapshot markupAmount). */
    incrementalAmount,
    baseBookingTotal: round2(base),
    totalAgentPays: totalYouPay,
    markupFeatureEnabledAtAdd: !!markupEnabled
  }
}

export function buildAgentCommissionSnapshot({
  baseBookingTotal,
  markupPercentage,
  markupEnabled,
  selectionId = null,
  defaultCommissionPct = GDS_DEFAULT_COMMISSION_PCT,
  /**
   * When true, the cart item's `totalWithProcessingFees` already includes the markup amount,
   * so downstream totals should not add markup again.
   */
  payableIncludesMarkup = false,
  /** Optional totals for convenience/checkout payloads. */
  payableTotalBeforeProcessingFees = null,
  payableTotalWithProcessingFees = null
}) {
  const b = getAgentMarkupBreakdown(baseBookingTotal, markupPercentage, markupEnabled, {
    defaultCommissionPct
  })
  return {
    markupPercentage: b.markupPercentage,
    defaultCommissionPct: b.defaultCommissionPct,
    defaultCommissionAmount: b.defaultCommissionAmount,
    totalCommissionAmount: b.totalCommissionAmount,
    /** Incremental $ only — added to base for total payable (not full % of base). */
    markupAmount: b.incrementalAmount,
    baseBookingTotal: b.baseBookingTotal,
    totalAgentPays: b.totalAgentPays,
    markupFeatureEnabledAtAdd: !!markupEnabled,
    selectionIdAtAdd: selectionId || null,
    payableIncludesMarkup: !!payableIncludesMarkup,
    payableTotalBeforeProcessingFees:
      payableTotalBeforeProcessingFees == null ? null : round2(Number(payableTotalBeforeProcessingFees) || 0),
    payableTotalWithProcessingFees:
      payableTotalWithProcessingFees == null ? null : round2(Number(payableTotalWithProcessingFees) || 0)
  }
}

/** Total payable for a cart line (hotel: base + incremental markup when snapshot exists). */
export function getHotelCartLinePayable(item) {
  if (!item || item.type === 'experience') return item?.totalWithProcessingFees || 0
  const base = Number(item.totalWithProcessingFees ?? item.price ?? 0) || 0
  const ac = item.agentCommission
  if (!ac || typeof ac !== 'object') return base

  // Legacy compatibility: if the cart line already persists a fee-inclusive, markup-inclusive total,
  // always charge that amount (avoid adding markup again in the UI).
  const twf = Number(item.totalWithProcessingFees ?? item.price ?? 0) || 0
  const tbf = Number(item.totalBeforeProcessingFees ?? 0) || 0
  const hasFeeSnapshot =
    Number(
      item?.processingFees?.totalProcessingFees ??
        item?.priceDetails?.processingFees?.totalProcessingFees ??
        item?.rateDetails?.processingFees?.totalProcessingFees ??
        item?.totalProcessingFees ??
        item?.priceDetails?.additionalFees ??
        0
    ) > 0
  if (hasFeeSnapshot && twf > 0 && tbf > 0) {
    // If totalBeforeProcessingFees already equals the commission-derived "you pay" total,
    // then totalWithProcessingFees includes fees and should be used as payable.
    if (Math.abs(tbf - Number(ac.totalAgentPays || 0)) <= 0.02) return twf
  }

  // If this item was persisted with markup already applied to totals, don't add it again.
  if (ac.payableIncludesMarkup) return base

  const floor =
    ac.markupBaselinePct != null && !Number.isNaN(Number(ac.markupBaselinePct))
      ? Number(ac.markupBaselinePct)
      : resolveDefaultCommissionPct({
          defaultCommissionPct: ac.defaultCommissionPct,
          rateSource: item.rateSource,
          isOffMarketStay: item.isOffMarketStay === true
        })
  const pct = clampAgentMarkupPct(ac.markupPercentage, floor)
  const incrementalPct = getIncrementalMarkupPct(pct, floor)
  const enabled = ac.markupFeatureEnabledAtAdd !== false
  const incrementalAmount = enabled ? round2((base * incrementalPct) / 100) : 0
  return round2(base + incrementalAmount)
}

/** Stripe / processing fees for one hotel cart line (matches checkout summary logic). */
export function getHotelCartItemProcessingFees(item, payableOverride) {
  const explicit =
    Number(
      item?.processingFees?.totalProcessingFees ??
        item?.priceDetails?.processingFees?.totalProcessingFees ??
        item?.rateDetails?.processingFees?.totalProcessingFees ??
        item?.priceDetails?.additionalFees
    ) || 0
  const payable = Number(payableOverride ?? getHotelCartLinePayable(item) ?? 0)
  const base = Number(item?.priceDetails?.baseRate || 0)
  const taxAmount = Number(item?.priceDetails?.tax || 0)
  const markup = Number(item?.agentCommission?.markupAmount || 0)
  const derived = payable - base - taxAmount - markup

  if (Number.isFinite(explicit) && explicit > 0) {
    if (Number.isFinite(derived) && derived > 0 && Math.abs(derived - explicit) > 0.02) {
      return round2(derived)
    }
    return round2(explicit)
  }

  return Number.isFinite(derived) && derived > 0 ? round2(derived) : 0
}

/**
 * Per-line price breakdown aligned with checkout "Cost Breakdown" sidebar.
 * @param {Function} [getPayable] - Optional override (e.g. QA promo charged amount).
 */
export function getHotelCartItemPriceBreakdown(item, getPayable = getHotelCartLinePayable) {
  const currency = item?.currency || 'USD'
  const baseAmount = round2(Number(item?.priceDetails?.baseRate || 0))
  const taxAmount = round2(Number(item?.priceDetails?.tax || 0))
  const markupAmount = round2(Number(item?.agentCommission?.markupAmount || 0))
  const totalPayable = round2(Number(getPayable(item) || 0))
  const processingFees = getHotelCartItemProcessingFees(item, totalPayable)
  const taxAndFees = round2(taxAmount + processingFees)

  return {
    currency,
    baseAmount,
    taxAmount,
    processingFees,
    taxAndFees,
    markupAmount,
    totalPayable
  }
}
